import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Loader2, CheckCircle2, XCircle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_ICON = { SS: '🤝', SF: '🎯', SD: '📜', SW: '🛡️', SWP: '🛡️', USPSP: '📮' };

export default function ProgramsPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [catalog, setCatalog] = useState(null);
  const [value, setValue] = useState(2500);
  const [country, setCountry] = useState('US');
  const [carrier, setCarrier] = useState('any');
  const [channel, setChannel] = useState('retail');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/programs/catalog`);
      const d = await r.json();
      setCatalog(d);
    })();
  }, []);

  const runQuote = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/programs/quote`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({
          declared_value: Number(value),
          destination_country: country,
          carrier: carrier === 'any' ? null : carrier,
          channel,
        }),
      });
      setQuote(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) runQuote(); /* eslint-disable-next-line */ }, [token]);

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in" data-testid="programs-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-indigo-500 font-semibold">The Shipping Risk Exchange™</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-1">
          SelleSend Shipping Assurance™
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Choose your carrier. Choose your service. Choose your protection. Choose how much risk you keep.
          Five programs · three channels · one bill. Powered by <b>SelleSend RiskChoice™</b>.
        </p>
      </div>

      {/* Quote console */}
      <Card className="border-indigo-100 dark:border-indigo-900/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500" /> Live Quote — All Five Programs
          </CardTitle>
          <CardDescription>Change the value or destination — every product re-prices instantly.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-5 gap-3 items-end">
            <div>
              <Label>Declared value ($)</Label>
              <Input type="number" min="1" step="1" value={value} onChange={e => setValue(e.target.value)} data-testid="value-input" />
            </div>
            <div>
              <Label>Destination</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">Domestic (US)</SelectItem>
                  <SelectItem value="CA">International (CA)</SelectItem>
                  <SelectItem value="UK">International (UK)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Carrier</Label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger data-testid="carrier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any (non-USPS)</SelectItem>
                  <SelectItem value="usps">USPS (isolated)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="wholesale">Wholesale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={runQuote} disabled={loading} className="w-full" data-testid="quote-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Re-quote
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quote results */}
      {quote && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="quote-results">
          {quote.options.map(opt => (
            <Card key={opt.code} className={opt.eligible ? 'border-emerald-200 dark:border-emerald-900/40' : 'border-slate-200 opacity-70'} data-testid={`product-card-${opt.code}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <span className="flex items-center gap-2">
                    <span className="text-lg">{PRODUCT_ICON[opt.code]}</span>
                    {opt.trademark}
                  </span>
                  {opt.eligible ? (
                    <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Eligible</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-500"><XCircle className="w-3 h-3 mr-1" /> N/A</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {opt.eligible ? (
                  <>
                    <div className="text-3xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">${opt.price?.toFixed(2)}</div>
                    <div className="text-xs text-slate-500 space-y-0.5">
                      {opt.rate_per_100 && <div>Rate: ${opt.rate_per_100}/$100</div>}
                      {opt.band_up_to && <div>Band: up to ${opt.band_up_to.toLocaleString()}</div>}
                      {opt.base && <div>Base: ${opt.base.toFixed(2)} + ${opt.excess_rate_per_100}/$100 over $10k</div>}
                      {opt.effective_rate !== undefined && (
                        <div>Effective rate: {(opt.effective_rate * 100).toFixed(2)}% {opt.capped && <b className="text-amber-600">(capped at 3%)</b>}</div>
                      )}
                      {opt.loss_share && (
                        <div>Loss share: <b>{Math.round(opt.loss_share.merchant * 100)}% merchant / {Math.round(opt.loss_share.sellesend * 100)}% SelleSend</b></div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-500 py-4">{opt.reason}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Underwriting context */}
      {quote && !quote.usps_isolated && (
        <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900/50 text-sm">
          <div><b>Underwriting:</b> {quote.ap_level} · <b>Channel:</b> {quote.channel} · <b>Destination:</b> {quote.international ? 'International' : 'Domestic'}</div>
        </div>
      )}
      {quote?.usps_isolated && (
        <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          <b>USPS-only carrier:</b> {quote.note}
        </div>
      )}

      {/* Catalog summary */}
      {catalog && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">The Programs</CardTitle>
            <CardDescription>All five sit under {catalog.umbrella}, powered by {catalog.engine}.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            <ul className="space-y-1.5">
              {catalog.products.filter(p => p.code !== 'USPSP').map(p => (
                <li key={p.code} className="flex items-start gap-3">
                  <span className="text-lg">{PRODUCT_ICON[p.code]}</span>
                  <div>
                    <b>{p.trademark}</b> — {p.position}
                    <div className="text-slate-500 text-xs">{p.tagline}</div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
